import type { ConversationItem, PeerId } from '../../contexts/ChatContext';

export const ASSISTANT_PEER_ID: PeerId = 'assistant';

export type ChannelKind = 'assistant' | 'tenant' | 'sms' | 'whatsapp' | 'service_desk';

export interface SwitcherRow {
  id: PeerId;
  kind: ChannelKind;
  name: string;
  preview: string;
  time?: number;
  unreadCount: number;
  isActive: boolean;
}

export interface AssistantSummary {
  /** Plain-text preview of the assistant's last message, if any. */
  preview?: string;
  time?: number;
}

export const ASSISTANT_EMPTY_PREVIEW = 'Ask about your plots, tenants and payments';

/** Which channel a peer id belongs to; unknown ids are treated as tenant chats. */
export function channelKindFor(peerId: PeerId): ChannelKind {
  if (peerId === ASSISTANT_PEER_ID) return 'assistant';
  if (peerId.startsWith('sms:')) return 'sms';
  if (peerId.startsWith('whatsapp:')) return 'whatsapp';
  if (peerId.startsWith('service_desk:')) return 'service_desk';
  return 'tenant';
}

/** The contact id inside tenant:/sms:/whatsapp: peer ids. */
export function contactIdFor(peerId: PeerId): string | null {
  const match = /^(?:tenant|sms|whatsapp):(.+)$/.exec(peerId);
  return match?.[1] ?? null;
}

/** One-line preview text: no markdown markers, nav links reduced to their label. */
export function plainPreview(text: string | undefined, max = 80): string {
  if (!text) return '';
  const flat = text
    .replace(/\[\[nav:[^|\]]+\|([^\]]+)\]\]/g, '$1')
    .replace(/\*\*|__|`/g, '')
    .replace(/^\s*(?:[-*•]|\d+[.)])\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
  return flat.length > max ? `${flat.slice(0, max - 1).trimEnd()}…` : flat;
}

/**
 * Builds the switcher rows. The Assistant is always first (even when the
 * conversation list has no assistant entry yet); everything else follows by
 * most recent message, then name, so a quiet chat never jumps around.
 */
export function buildSwitcherRows(
  conversations: ConversationItem[],
  activePeerId: PeerId | null,
  assistant: AssistantSummary = {},
): SwitcherRow[] {
  const active = activePeerId ?? ASSISTANT_PEER_ID;
  const seen = new Set<string>([ASSISTANT_PEER_ID]);
  const others: SwitcherRow[] = [];
  for (const c of conversations) {
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    others.push({
      id: c.id,
      kind: channelKindFor(c.id),
      name: c.name,
      preview: plainPreview(c.lastMessage),
      time: c.lastMessageTime,
      unreadCount: c.unreadCount || 0,
      isActive: c.id === active,
    });
  }
  others.sort((a, b) => (b.time ?? 0) - (a.time ?? 0) || a.name.localeCompare(b.name));
  const pinned: SwitcherRow = {
    id: ASSISTANT_PEER_ID,
    kind: 'assistant',
    name: 'Re:Loam Assistant',
    preview: assistant.preview || ASSISTANT_EMPTY_PREVIEW,
    time: assistant.time,
    unreadCount: 0,
    isActive: active === ASSISTANT_PEER_ID,
  };
  return [pinned, ...others];
}

/** "14:05" for today, "Yesterday", otherwise "12 Oct". */
export function formatConversationTime(timestamp: number | undefined, now: Date = new Date()): string {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '';
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (days <= 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
